import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Avatar, Chip, CircularProgress
} from '@mui/material';
import { 
  Add, 
  Delete, 
  Person, 
  Email, 
  Phone, 
  Badge, 
  Event, 
  Business 
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import ModernCard, { StatsCard } from '../common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from '../common/ModernButton';
import axiosInstance from '../axiosConfig';
import { useLanguage } from '../../contexts/LanguageContext';

const Visitors = () => {
  const { translate } = useLanguage();
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    identification_number: '',
    visit_reason: '',
    visit_date: ''
  });

  useEffect(() => {
    fetchVisitors();
  }, []);

  // Obtener visitantes del proyecto actual
  const fetchVisitors = async () => {
    const projectId = localStorage.getItem('projectId');

    if (!projectId) {
      console.error('No hay projectId guardado en localStorage');
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/projects/${projectId}/visitors`);
      setVisitors(response.data);
    } catch (error) {
      console.error('Error fetching visitors:', error);
    } finally {
      setLoading(false);
    }
  };

  // Abrir/Cerrar el modal de agregar visitante
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormData({ full_name: '', email: '', phone: '', identification_number: '', visit_reason: '', visit_date: '' });
  };

  // Capturar cambios en los inputs
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Guardar nuevo visitante
  const handleSave = async () => {
    const projectId = localStorage.getItem('projectId');

    if (!projectId) {
      console.error('No hay projectId guardado en localStorage');
      return;
    }

    if (!formData.full_name || !formData.email || !formData.identification_number || !formData.visit_reason || !formData.visit_date) {
      console.error('Todos los campos son obligatorios');
      return;
    }

    try {
      await axiosInstance.post(`/api/projects/${projectId}/visitors/register`, formData);
      fetchVisitors();
      handleClose();
    } catch (error) {
      console.error('Error saving visitor:', error);
    }
  };

  // Eliminar visitante
  const handleDeleteVisitor = async (visitorId) => {
    const projectId = localStorage.getItem('projectId');

    if (!projectId) {
      console.error('No hay projectId guardado en localStorage');
      return;
    }

    try {
      await axiosInstance.delete(`/api/projects/${projectId}/visitors/${visitorId}`);
      fetchVisitors();
    } catch (error) {
      console.error('Error deleting visitor:', error);
    }
  };

  const stats = {
    total: visitors.length,
    today: visitors.filter(v => {
      const today = new Date().toISOString().split('T')[0];
      return v.visit_date === today;
    }).length,
    thisWeek: visitors.filter(v => {
      const today = new Date();
      const visitDate = new Date(v.visit_date);
      const diffTime = Math.abs(today - visitDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 7;
    }).length,
    thisMonth: visitors.filter(v => {
      const today = new Date();
      const visitDate = new Date(v.visit_date);
      return today.getMonth() === visitDate.getMonth() && today.getFullYear() === visitDate.getFullYear();
    }).length
  };

  if (loading) {
    return (
      <ModernLayout title={translate('visitors.title')}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </ModernLayout>
    );
  }

  return (
    <ModernLayout title={translate('visitors.title')}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {translate('visitors.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('visitors.subtitle')}
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('visitors.totalVisitors')}
            value={stats.total}
            subtitle={translate('visitors.registeredInSystem')}
            icon={<Person />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('visitors.today')}
            value={stats.today}
            subtitle={translate('visitors.todayVisitors')}
            icon={<Event />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('visitors.thisWeek')}
            value={stats.thisWeek}
            subtitle={translate('visitors.last7Days')}
            icon={<Event />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title={translate('visitors.thisMonth')}
            value={stats.thisMonth}
            subtitle={translate('visitors.monthVisitors')}
            icon={<Business />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Actions Bar */}
      <ModernCard
        title={translate('visitors.actions')}
        subtitle={translate('visitors.manageVisitors')}
        sx={{ mb: 3 }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <GradientButton
            startIcon={<Add />}
            onClick={handleOpen}
          >
            {translate('visitors.addVisitor')}
          </GradientButton>
          <GlassButton
            variant="outlined"
            onClick={fetchVisitors}
          >
            Actualizar Lista
          </GlassButton>
        </Box>
      </ModernCard>

      {/* Visitors Table */}
      <ModernCard
        title={translate('visitors.visitorsList')}
        subtitle={`${translate('visitors.showing')} ${visitors.length} ${translate('visitors.visitors')}`}
      >
        <TableContainer sx={{ maxHeight: 600, overflow: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{translate('visitors.visitor')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('visitors.identification')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('visitors.visitReason')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('visitors.visitDate')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('visitors.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visitors.length > 0 ? (
                visitors.map((visitor) => (
                  <TableRow key={visitor.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          width: 40,
                          height: 40
                        }}>
                          {visitor.full_name?.charAt(0) || 'V'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {visitor.full_name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Email fontSize="small" color="action" />
                          {visitor.email}
                        </Typography>
                        {visitor.phone && (
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Phone fontSize="small" color="action" />
                            {visitor.phone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Badge fontSize="small" color="action" />
                        <Typography variant="body2">
                          {visitor.identification_number}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={visitor.visit_reason}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(visitor.visit_date).toLocaleDateString('es-ES')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteVisitor(visitor.id)}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.1)',
                          }
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Person sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No hay visitantes registrados
                      </Typography>
                      <GradientButton
                        startIcon={<Add />}
                        onClick={handleOpen}
                      >
                        Agregar Primer Visitante
                      </GradientButton>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </ModernCard>

      {/* Modal de Creación */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        fullWidth 
        maxWidth="sm"
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {translate('visitors.addVisitor')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField 
              name="full_name" 
              label={translate('visitors.fullName')} 
              fullWidth 
              value={formData.full_name} 
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                },
              }}
            />
            <TextField 
              name="email" 
              label={translate('visitors.email')} 
              fullWidth 
              value={formData.email} 
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                },
              }}
            />
            <TextField 
              name="phone" 
              label={translate('visitors.phone')} 
              fullWidth 
              value={formData.phone} 
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                },
              }}
            />
            <TextField 
              name="identification_number" 
              label={translate('visitors.identificationNumber')} 
              fullWidth 
              value={formData.identification_number} 
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                },
              }}
            />
            <TextField 
              name="visit_reason" 
              label={translate('visitors.visitReason')} 
              fullWidth 
              value={formData.visit_reason} 
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                },
              }}
            />
            <TextField 
              name="visit_date" 
              label={translate('visitors.visitDate')} 
              type="date" 
              fullWidth 
              value={formData.visit_date} 
              onChange={handleChange} 
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={handleClose}>
            {translate('visitors.cancel')}
          </GlassButton>
          <GradientButton onClick={handleSave}>
            {translate('visitors.save')}
          </GradientButton>
        </DialogActions>
      </Dialog>
    </ModernLayout>
  );
};

export default Visitors;
